import React, { useState, useEffect } from 'react';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function MiniCalendar({ selectedDate, onChange, allowPastDates = false }) {
  const [blockedDates, setBlockedDates] = useState({});
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    fetchBlockedDates();
  }, []);

  // เลื่อนเดือนของปฏิทินให้ตรงกับวันนัดหมายของลูกค้าโดยอัตโนมัติ
  useEffect(() => {
    if (selectedDate) {
      const parsed = new Date(selectedDate);
      if (!isNaN(parsed.getTime())) {
        setCurrentDate(parsed);
      }
    }
  }, [selectedDate]);

  const fetchBlockedDates = async () => {
    try {
      const res = await fetch(`${API_URL}?action=get_blocked_dates`);
      const data = await res.json();
      if (data.success && data.data) {
        const mapped = {};
        data.data.forEach(item => {
          mapped[item.blocked_date] = item.reason;
        });
        setBlockedDates(mapped);
      }
    } catch (err) {
      console.error("Failed to load blocked dates:", err);
    }
  };

  const thaiMonths = [
    "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
    "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"
  ];
  const dayNames = ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"];

  const firstDayIndex = new Date(year, month, 1).getDay();
  const lastDay = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => {
    const today = new Date();
    if (!allowPastDates) {
      if (year < today.getFullYear() || (year === today.getFullYear() && month <= today.getMonth())) {
        return; // Don't go to past months if not allowed
      }
    }
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const renderDays = () => {
    const cells = [];
    
    // Empty cells for padding
    for (let i = 0; i < firstDayIndex; i++) {
      cells.push(<div key={`empty-${i}`} className="h-10"></div>);
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let d = 1; d <= lastDay; d++) {
      const dateObj = new Date(year, month, d);
      const mStr = String(month + 1).padStart(2, '0');
      const dStr = String(d).padStart(2, '0');
      const dateStr = `${year}-${mStr}-${dStr}`;

      const isPast = dateObj < today;
      const isBlocked = blockedDates.hasOwnProperty(dateStr);
      const isSelected = dateStr === selectedDate;

      let cellClass = "flex flex-col items-center justify-center h-10 w-full rounded-lg text-xs font-bold transition-all relative ";
      let subtext = "";
      let isDisabled = false;

      if (isSelected) {
        cellClass = "flex flex-col items-center justify-center h-10 w-full rounded-lg text-xs font-black bg-red-600 text-white shadow-md shadow-red-600/30 cursor-pointer scale-105 border border-red-500";
        subtext = isBlocked ? "เต็ม" : "เลือก";
      } else if (!allowPastDates && isPast) {
        cellClass += "text-gray-300 dark:text-gray-700 cursor-not-allowed opacity-40";
        isDisabled = true;
      } else if (isBlocked) {
        cellClass += "bg-red-500/10 text-red-500 dark:text-red-400 border border-red-200/20 cursor-not-allowed line-through";
        subtext = "เต็ม";
        isDisabled = true;
      } else {
        cellClass += "bg-emerald-500/5 hover:bg-red-500/10 text-gray-700 dark:text-gray-300 hover:text-red-500 border border-emerald-500/10 cursor-pointer ";
        subtext = "ว่าง";
      }

      cells.push(
        <button
          key={`day-${d}`}
          type="button"
          disabled={isDisabled}
          onClick={() => {
            if (!isDisabled && onChange) {
              onChange(dateStr);
            }
          }}
          className={cellClass}
          title={isBlocked ? (blockedDates[dateStr] || 'คิวงานเต็ม') : ''}
        >
          <span>{d}</span>
          {subtext && (!isPast || isSelected) && (
            <span className={`text-[7px] -mt-0.5 ${isSelected ? 'text-red-100' : isBlocked ? 'text-red-500' : 'text-emerald-500'}`}>
              {subtext}
            </span>
          )}
        </button>
      );
    }

    return cells;
  };

  return (
    <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] p-4 rounded-2xl w-full text-left transition-all">
      <div className="flex justify-between items-center mb-4">
        <button 
          type="button" 
          onClick={prevMonth}
          className="p-1.5 bg-gray-100 dark:bg-[#1b2132] hover:bg-gray-200 dark:hover:bg-[#252c42] rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-850 dark:hover:text-white transition font-mono text-xs"
        >
          ◀
        </button>
        <span className="text-xs font-extrabold text-gray-800 dark:text-white">
          {thaiMonths[month]} {year + 543}
        </span>
        <button 
          type="button" 
          onClick={nextMonth}
          className="p-1.5 bg-gray-100 dark:bg-[#1b2132] hover:bg-gray-200 dark:hover:bg-[#252c42] rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-850 dark:hover:text-white transition font-mono text-xs"
        >
          ▶
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-gray-400 dark:text-gray-500 mb-2">
        {dayNames.map((d, i) => (
          <div key={i} className={i === 0 || i === 6 ? 'text-red-500/80' : ''}>{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {renderDays()}
      </div>
    </div>
  );
}
